/* Banking Portal · Waves 19/20/22 (Feb 2026).
   Three pages in one file:
     Wave-19 · /banking/model-risk            — SR 11-7 Model Inventory
     Wave-20 · /banking/bsa-officer            — BSA Officer Console
     Wave-22 · /banking/sanctions-coverage     — Multi-list sanctions coverage
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Cpu, BookOpen, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, FileCheck2,
  Clock, Globe, Briefcase, Layers, Filter, RefreshCw, ChevronRight,
  Scale, Hash, AlertOctagon, Building2, Mail, FileSearch,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}
function money(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function dateOnly(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

const TONE_CLS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  slate:   "bg-slate-50 text-slate-700 border-slate-200",
};

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const cls = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${cls} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-19 · MODEL RISK MANAGEMENT (SR 11-7)
// ═════════════════════════════════════════════════════════════════════════
export function BankingModelRisk() {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState("all");

  useEffect(() => {
    const qs = tier === "all" ? "" : `?tier=${tier}`;
    api.get(`/partner/banking/model-risk/inventory${qs}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load model inventory"));
  }, [tier]);

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="banking-model-risk">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Cpu className="w-6 h-6 text-indigo-500" />
            Model Risk Management
          </h1>
          <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">SR 11-7</Badge>
          <Badge variant="outline" className="text-slate-500">{data.framework}</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Every decision model in Verqo's stack — credit, AML, fraud, KYB, sanctions — under formal governance.
          Sponsor banks can audit version · owner · methodology · validation cadence per model.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total models"      value={fmt(data.total_models)} sub="In governed inventory" icon={Layers} testid="kpi-models-total" />
        <Kpi label="Tier-1 critical"   value={fmt(data.by_tier.tier_1_critical)} sub="Direct credit/AML impact" tone="amber" icon={ShieldAlert} testid="kpi-models-tier1" />
        <Kpi label="Tier-2 supporting" value={fmt(data.by_tier.tier_2_supporting)} sub="Supporting decisions" tone="sky" icon={Cpu} testid="kpi-models-tier2" />
        <Kpi label="Disclosed gaps"    value={fmt(data.honest_gaps?.length || 0)} sub="Honestly tracked" tone="rose" icon={AlertTriangle} testid="kpi-models-gaps" />
      </div>

      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-slate-400" />
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-44" data-testid="select-model-tier"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="1">Tier 1 — Critical</SelectItem>
            <SelectItem value="2">Tier 2 — Supporting</SelectItem>
            <SelectItem value="3">Tier 3 — Informational</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500">{data.rows.length} model{data.rows.length === 1 ? "" : "s"}</div>
      </div>

      <div className="space-y-3">
        {data.rows.map(m => (
          <div key={m.model_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`model-card-${m.model_id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold text-slate-900 flex items-center gap-2">
                  {m.name}
                  <Badge variant="outline" className="text-[10.5px]">v{m.version}</Badge>
                  <Badge className={
                    m.tier === 1 ? "bg-rose-50 text-rose-700 border-rose-200"
                    : m.tier === 2 ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-50 text-slate-600 border-slate-200"
                  }>Tier {m.tier}</Badge>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {m.category.replaceAll("_", " ")} · owned by {m.owner}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10.5px] text-slate-500 uppercase tracking-wider">Last validated</div>
                <div className="text-sm tabular-nums text-slate-700">{dateOnly(m.last_validated)}</div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">{m.validation_cadence}</div>
              </div>
            </div>

            <div className="mt-3 text-[12px] text-slate-700">
              <span className="font-medium text-slate-800">Business decision:</span> {m.business_decision}
            </div>

            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11.5px]">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Inputs</div>
                <div className="text-slate-700 mt-0.5 break-words">{m.inputs.slice(0, 6).join(" · ")}{m.inputs.length > 6 ? "…" : ""}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Outputs</div>
                <div className="text-slate-700 mt-0.5 break-words">{m.outputs.join(" · ")}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Mechanics</div>
                <div className="text-slate-700 mt-0.5">{m.ai_or_rules.replaceAll("_", " ")}</div>
              </div>
            </div>

            <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" />{m.methodology_doc}</span>
              {m.drift_metric && <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />drift: {m.drift_metric}</span>}
              {m.fired_count_30d != null && <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" />{fmt(m.fired_count_30d)} fires (30d)</span>}
              {m.sub_models && <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" />{m.sub_models.length} sub-rules</span>}
            </div>

            <div className="mt-2 text-[11px] text-slate-600 italic">Governance: {m.governance}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4" data-testid="model-risk-gaps">
        <div className="flex items-center gap-2 font-semibold text-rose-900">
          <AlertTriangle className="w-4 h-4" /> Honest gaps · disclosed
        </div>
        <ul className="mt-2 space-y-1 text-[12px] text-rose-900">
          {data.honest_gaps.map((g, i) => (
            <li key={i} className="flex items-start gap-2">
              <Badge className={TONE_CLS[g.severity === "high" ? "rose" : g.severity === "medium" ? "amber" : "slate"]}>{g.severity}</Badge>
              <div>
                <span className="font-medium">{g.gap}</span> — {g.detail}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Wave-20 · BSA OFFICER CONSOLE
// ═════════════════════════════════════════════════════════════════════════
export function BankingBsaOfficer() {
  const [sar, setSar]   = useState(null);
  const [ctr, setCtr]   = useState(null);
  const [log314a, setLog314a] = useState(null);
  const [log314b, setLog314b] = useState(null);
  const [ofac, setOfac] = useState(null);
  const [severity, setSeverity] = useState("all");

  useEffect(() => {
    const q = severity === "all" ? "" : `?severity=${severity}`;
    api.get(`/partner/banking/bsa/sar-pipeline${q}`).then(r => setSar(r.data)).catch(() => toast.error("SAR pipeline failed"));
  }, [severity]);

  useEffect(() => {
    api.get("/partner/banking/bsa/ctr-aggregates?days=30").then(r => setCtr(r.data)).catch(() => toast.error("CTR aggregates failed"));
    api.get("/partner/banking/bsa/314a-log").then(r => setLog314a(r.data)).catch(() => {});
    api.get("/partner/banking/bsa/314b-log").then(r => setLog314b(r.data)).catch(() => {});
    api.get("/partner/banking/bsa/ofac-risk-rating").then(r => setOfac(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="banking-bsa-officer">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Scale className="w-6 h-6 text-amber-500" />
            BSA Officer Console
          </h1>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200">FFIEC BSA/AML Manual</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Designated BSA-officer workbench: SAR filing clock · CTR auto-aggregation · 314(a)/314(b) processing · institutional OFAC risk rating.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="SAR — open"       value={fmt(sar?.open_count || 0)}     sub="Awaiting filing" tone="amber"   icon={FileSearch} testid="kpi-sar-open" />
        <Kpi label="SAR — overdue"    value={fmt(sar?.overdue_count || 0)}  sub=">30 days · file now" tone="rose" icon={AlertOctagon} testid="kpi-sar-overdue" />
        <Kpi label="SAR — filed 30d"  value={fmt(sar?.sars_filed_30d || 0)} sub="Closed loop" tone="emerald" icon={FileCheck2} testid="kpi-sar-filed" />
        <Kpi label="CTR — required"   value={fmt(ctr?.aggregates_count || 0)} sub="≥$10K aggregate / 24h" tone="amber" icon={Briefcase} testid="kpi-ctr-required" />
        <Kpi label="OFAC risk rating" value={(ofac?.rating || "—").toUpperCase()} sub={`${ofac?.total_score || 0}/25`} tone={ofac?.tone || "slate"} icon={Globe} testid="kpi-ofac-rating" />
      </div>

      <Tabs defaultValue="sar" className="w-full">
        <TabsList>
          <TabsTrigger value="sar" data-testid="tab-sar">SAR pipeline</TabsTrigger>
          <TabsTrigger value="ctr" data-testid="tab-ctr">CTR aggregator</TabsTrigger>
          <TabsTrigger value="314a" data-testid="tab-314a">314(a) log</TabsTrigger>
          <TabsTrigger value="314b" data-testid="tab-314b">314(b) log</TabsTrigger>
          <TabsTrigger value="ofac" data-testid="tab-ofac">OFAC rating</TabsTrigger>
        </TabsList>

        <TabsContent value="sar" className="pt-4 space-y-3">
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="w-44" data-testid="select-sar-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-[11.5px]">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2">Alert</th>
                  <th className="px-3 py-2">Customer (masked)</th>
                  <th className="px-3 py-2">Severity</th>
                  <th className="px-3 py-2">Age</th>
                  <th className="px-3 py-2">Filing deadline</th>
                </tr>
              </thead>
              <tbody>
                {(sar?.rows || []).map((r, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`sar-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-[10.5px] text-slate-600">{r.kind}</td>
                    <td className="px-3 py-2">
                      <div>{r.user_name_masked}</div>
                      <div className="text-[10px] text-slate-400">{r.user_email_masked} · {r.user_company}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={TONE_CLS[r.severity === "critical" || r.severity === "high" ? "rose" : r.severity === "medium" ? "amber" : "slate"]}>{r.severity}</Badge>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{r.age_days}d</td>
                    <td className="px-3 py-2">
                      <Badge className={TONE_CLS[r.deadline_tone]}>
                        {r.days_to_deadline < 0 ? `Overdue by ${-r.days_to_deadline}d` : `${r.days_to_deadline}d left`}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {!sar?.rows?.length && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No open SAR-eligible alerts.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-500">
            <Clock className="inline w-3 h-3 mr-1" />
            FinCEN deadline: SARs must be filed within 30 days of initial detection (60 days if no suspect identified).
          </div>
        </TabsContent>

        <TabsContent value="ctr" className="pt-4 space-y-3">
          <div className="text-[12px] text-slate-600">
            Customers whose aggregate transaction volume crossed <strong>${fmt(ctr?.threshold_usd || 10000)}</strong> within a 24-hour window in the last {ctr?.window_days || 30} days. CTR filing required for cash-equivalent transactions.
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-[11.5px]">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2">Customer (masked)</th>
                  <th className="px-3 py-2">Window</th>
                  <th className="px-3 py-2 text-right">Aggregate</th>
                  <th className="px-3 py-2 text-right">Exceeds by</th>
                </tr>
              </thead>
              <tbody>
                {(ctr?.rows || []).map((r, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`ctr-row-${i}`}>
                    <td className="px-3 py-2">
                      <div>{r.user_name_masked}</div>
                      <div className="text-[10px] text-slate-400">{r.user_email_masked} · {r.user_company}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10.5px] text-slate-600">{r.window_date_utc}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{money(r.aggregate_usd)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">+{money(r.exceeds_by_usd)}</td>
                  </tr>
                ))}
                {!ctr?.rows?.length && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No CTR-threshold aggregates in window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-500">CTRs filed (lifetime): {fmt(ctr?.ctr_filed_total || 0)}</div>
        </TabsContent>

        <TabsContent value="314a" className="pt-4 space-y-3">
          <div className="text-[12px] text-slate-600">{log314a?.scheme || "FinCEN 314(a) Information Request Processing"}</div>
          {log314a?.attestation && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 text-[12px] text-emerald-900">
              <CheckCircle2 className="inline w-4 h-4 mr-1" />{log314a.attestation}
            </div>
          )}
          <div className="text-[11px] text-slate-500">Retention: {log314a?.retention_days || 365} days · {fmt(log314a?.count || 0)} request{(log314a?.count || 0) === 1 ? "" : "s"} on record.</div>
        </TabsContent>

        <TabsContent value="314b" className="pt-4 space-y-3">
          <div className="text-[12px] text-slate-600">{log314b?.scheme || "FinCEN 314(b) Voluntary Information Sharing"}</div>
          {log314b?.attestation && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 text-[12px] text-emerald-900">
              <CheckCircle2 className="inline w-4 h-4 mr-1" />{log314b.attestation}
            </div>
          )}
          <div className="text-[11px] text-slate-500">{fmt(log314b?.count || 0)} info-sharing event{(log314b?.count || 0) === 1 ? "" : "s"} on record.</div>
        </TabsContent>

        <TabsContent value="ofac" className="pt-4 space-y-3">
          <div className="text-[12px] text-slate-600">{ofac?.framework || "OFAC FAQ 1004"}</div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500">Institutional rating</div>
                <div className="flex items-center gap-3 mt-1">
                  <Badge className={TONE_CLS[ofac?.tone || "slate"]}>{(ofac?.rating || "—").toUpperCase()}</Badge>
                  <div className="text-2xl tabular-nums font-semibold text-slate-900">{ofac?.total_score || 0}<span className="text-sm text-slate-400">/{ofac?.max_score || 25}</span></div>
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {(ofac?.dimensions || []).map(d => (
                <div key={d.key} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px]" data-testid={`ofac-dim-${d.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-slate-800">{d.label}</div>
                    <div className="tabular-nums text-slate-700">{d.score}/5</div>
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">{d.detail}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[10.5px] text-slate-500 italic">{ofac?.note}</div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Wave-22 · MULTI-LIST SANCTIONS COVERAGE
// ═════════════════════════════════════════════════════════════════════════
export function BankingSanctionsCoverage() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => api.get("/partner/banking/sanctions/coverage")
    .then(r => setData(r.data))
    .catch(() => toast.error("Sanctions coverage failed"));

  useEffect(() => { load(); }, []);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/partner/banking/sanctions/refresh-multi-list");
      toast.success("Refreshed multi-list counts");
      await load();
    } catch {
      toast.error("Refresh failed (network or upstream)");
    } finally {
      setRefreshing(false);
    }
  };

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="banking-sanctions-coverage">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Globe className="w-6 h-6 text-sky-500" />
            Sanctions Coverage
          </h1>
          <Badge className="bg-sky-50 text-sky-700 border-sky-200">FFIEC OFAC §</Badge>
          <Button size="sm" variant="outline" onClick={doRefresh} disabled={refreshing} data-testid="btn-refresh-lists">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh lists
          </Button>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Lists tracked"  value={fmt(data.total_lists)} sub="Across 4 jurisdictions" icon={Layers} testid="kpi-lists-total" />
        <Kpi label="Live full match" value={fmt(data.live_full_match)} sub="Fuzzy + exact" tone="emerald" icon={CheckCircle2} testid="kpi-lists-live" />
        <Kpi label="Counted only"   value={fmt(data.counted_only)} sub="Entry-count tracked" tone="amber" icon={Hash} testid="kpi-lists-counted" />
        <Kpi label="Disclosed gaps" value={fmt(data.disclosed_gaps)} sub="Honest gaps" tone="rose" icon={AlertTriangle} testid="kpi-lists-gaps" />
      </div>

      <div className="space-y-3">
        {data.lists.map(li => (
          <div key={li.list_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`list-card-${li.list_id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  {li.label}
                  <Badge className={TONE_CLS[li.tone]}>{li.status.replaceAll("_", " ")}</Badge>
                  {li.screen_match && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">match-against-customers</Badge>}
                </div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">{li.issuer}</div>
              </div>
              <div className="text-right">
                {li.entry_count != null && (
                  <>
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-500">Entries</div>
                    <div className="text-lg tabular-nums font-semibold text-slate-900">{fmt(li.entry_count)}</div>
                  </>
                )}
                {li.last_refresh && (
                  <div className="text-[10.5px] text-slate-500 mt-0.5">refreshed {dateOnly(li.last_refresh)}</div>
                )}
              </div>
            </div>
            <div className="mt-2 text-[11.5px] text-slate-600">{li.notes}</div>
            {li.error && <div className="mt-2 text-[10.5px] text-rose-600 font-mono">⚠ {li.error}</div>}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[12px] text-slate-700" data-testid="sanctions-disclaimer">
        <div className="font-semibold text-slate-800 flex items-center gap-2 mb-1.5">
          <FileCheck2 className="w-4 h-4" /> Honest coverage disclaimer
        </div>
        {data.disclaimer}
      </div>
    </div>
  );
}
